/**
 * AI Testing Dashboard Component
 * Real-time testing interface for baby boomer focused testing
 */

import React, { useState, useEffect, useRef } from 'react';
import aiTestingDashboardService, {
  DashboardTestAgent,
  DashboardTestSuite,
  DashboardStats,
  LogEntry as ServiceLogEntry
} from '../../services/aiTestingDashboardService';
import AITestingScheduler from '../AITestingScheduler';
import AITestingScenarioBuilder from '../AITestingScenarioBuilder';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert,
  Tab,
  Tabs,
  InputAdornment,
} from '@mui/material';
import MetricCard from '../common/MetricCard';
import StatusBadge from '../common/StatusBadge';
import { colors, spacing } from '../../theme/designTokens';
import {
  ShowChart as Activity,
  Error as AlertCircle,
  CheckCircle as CheckCircle2,
  AccessTime as Clock,
  Cloud,
  Description as FileText,
  Headphones,
  Refresh as Loader2,
  PlayArrow as Play,
  Refresh as RefreshCw,
  Search,
  Settings,
  TrendingUp,
  People as Users,
  VolumeUp as Volume2,
  Bolt as Zap,
  BarChart as BarChart3,
  Security as Shield,
  Memory as Cpu,
  Mic,
  PhotoCamera as Camera,
  Psychology as Brain,
  Favorite as Heart,
  Language as Globe,
  Layers,
  Terminal,
  MonitorHeart as MonitorCheck,
  Warning as AlertTriangle,
  PauseCircle,
  PlayCircle,
  ChevronRight,
  FilterList as Filter,
  Download,
  MoreVert as MoreVertical,
  Visibility as Eye
} from '@mui/icons-material';

interface TestAgent {
  id: string;
  name: string;
  category: string;
  icon: React.ComponentType<any>;
  description: string;
  health: number;
  is_active: boolean;
  capabilities: string[];
  configuration: Record<string, any>;
}

interface LogEntry {
  id: number;
  timestamp: string;
  level: 'info' | 'success' | 'warning' | 'error';
  message: string;
}

const AITestingDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [testProgress, setTestProgress] = useState(67);
  const [testAgents, setTestAgents] = useState<TestAgent[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [activeTestSuites, setActiveTestSuites] = useState<DashboardTestSuite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const websocketRef = useRef<WebSocket | null>(null);

  // Fetch data from API service
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all dashboard data in parallel
        const [agents, stats, suites] = await Promise.all([
          aiTestingDashboardService.getTestAgents(),
          aiTestingDashboardService.getDashboardStats(),
          aiTestingDashboardService.getActiveTestSuites()
        ]);
        
        // Transform agents to include icon components
        const agentsWithIcons = agents.map(agent => ({
          ...agent,
          icon: getIconComponent(agent.icon)
        }));
        
        setTestAgents(agentsWithIcons);
        setDashboardStats(stats);
        setActiveTestSuites(suites);
        
        // Set default selected agents (first 4 active agents)
        const defaultSelected = agentsWithIcons
          .filter(agent => agent.is_active)
          .slice(0, 4)
          .map(agent => agent.id);
        setSelectedAgents(defaultSelected);
        
        console.log('✅ Dashboard data loaded successfully');
      } catch (err) {
        console.error('❌ Failed to load dashboard data:', err);
        setError('Failed to load dashboard data. Using fallback data.');
        // Keep existing hardcoded data as fallback on error
        setTestAgents([
          { id: 'login_flow', name: 'Login Flow', category: 'auth', icon: Shield, description: 'Authentication, OAuth, password reset', health: 98, is_active: true, capabilities: [], configuration: {} },
          { id: 'voice_recording', name: 'Voice Recording', category: 'voice', icon: Mic, description: 'Audio quality, transcription', health: 92, is_active: true, capabilities: [], configuration: {} },
          { id: 'chapter_creation', name: 'Chapter Creation', category: 'content', icon: FileText, description: 'Content creation, AI styles', health: 95, is_active: true, capabilities: [], configuration: {} },
          { id: 'photo_album', name: 'Photo Album', category: 'media', icon: Camera, description: 'Upload, organization, AI narrative', health: 88, is_active: true, capabilities: [], configuration: {} },
          { id: 'family_collaboration', name: 'Family Collaboration', category: 'social', icon: Users, description: 'Multi-user features, permissions', health: 94, is_active: true, capabilities: [], configuration: {} },
          { id: 'payment_subscription', name: 'Payment & Subscription', category: 'commerce', icon: Zap, description: 'IAP, subscriptions, receipts', health: 90, is_active: true, capabilities: [], configuration: {} }
        ]);
        setSelectedAgents(['login_flow', 'chapter_creation', 'payment_subscription', 'family_collaboration']);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  // Simulate real-time logs
  useEffect(() => {
    const interval = setInterval(() => {
      if (isRunning) {
        const sampleLogs = [
          { level: 'info' as const, message: 'LoginFlowAgent: Testing OAuth integration with Google' },
          { level: 'success' as const, message: '✓ Voice recording quality test passed - 98% clarity' },
          { level: 'info' as const, message: 'ChapterCreationAgent: Applying Hemingway style transformation' },
          { level: 'warning' as const, message: '⚠ Slow response time detected in photo upload (3.2s)' },
          { level: 'error' as const, message: '✗ Payment validation timeout - retrying with extended timeout' },
          { level: 'success' as const, message: '✓ Family member invitation email sent successfully' },
          { level: 'info' as const, message: 'LifeBookAgent: Generating PDF with 47 chapters' },
        ];
        
        const randomLog = sampleLogs[Math.floor(Math.random() * sampleLogs.length)];
        addLog(randomLog.level, randomLog.message);
        
        // Update progress
        setTestProgress(prev => Math.min(prev + Math.random() * 5, 100));
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isRunning]);

  // Cleanup WebSocket connections on unmount
  useEffect(() => {
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
        console.log('🔗 WebSocket connection cleaned up');
      }
    };
  }, []);

  const addLog = (level: 'info' | 'success' | 'warning' | 'error', message: string) => {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
    setLogs(prev => [...prev, { timestamp, level, message, id: Date.now() }]);
    
    // Scroll to bottom
    if (logContainerRef.current) {
      setTimeout(() => {
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  const runTestSuite = async () => {
    if (selectedAgents.length === 0) {
      addLog('error', 'No agents selected for testing');
      return;
    }
    
    setIsRunning(true);
    setTestProgress(0);
    addLog('info', `Executing test suite with ${selectedAgents.length} selected agents...`);
    
    try {
      const result = await aiTestingDashboardService.executeTestSuite({
        suite_name: 'Complete User Journey Test',
        target_url: 'https://app.lifescribe.com',
        platform: 'all',
        environment: 'dev',
        test_type: 'full_journey',
        selected_agents: selectedAgents
      });
      
      if (result.success) {
        addLog('success', `Test suite execution started successfully. Run ID: ${result.test_run_id}`);
        if (result.test_run_id) {
          // Start monitoring the test run
          monitorTestRun(result.test_run_id);
        }
      } else {
        addLog('error', `Failed to start test suite: ${result.message}`);
        setIsRunning(false);
      }
    } catch (error) {
      console.error('Failed to execute test suite:', error);
      addLog('error', 'Failed to start test suite execution');
      setIsRunning(false);
    }
  };
  
  const monitorTestRun = (testRunId: string) => {
    // Get WebSocket URL for real-time monitoring
    const wsUrl = aiTestingDashboardService.getWebSocketUrl(testRunId);
    
    if (wsUrl) {
      try {
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          addLog('info', `Connected to real-time monitoring for test run ${testRunId}`);
          console.log('🔗 WebSocket connected for test monitoring');
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Handle different message types
            switch (data.type) {
              case 'progress':
                setTestProgress(data.progress || 0);
                addLog('info', `Test progress: ${data.progress}% - ${data.message}`);
                break;
              case 'agent_status':
                addLog('info', `${data.agent_name}: ${data.status} - ${data.message}`);
                break;
              case 'test_complete':
                setIsRunning(false);
                setTestProgress(100);
                addLog('success', `Test suite completed: ${data.message}`);
                refreshActiveTestSuites();
                ws.close();
                break;
              case 'test_failed':
                setIsRunning(false);
                addLog('error', `Test suite failed: ${data.message}`);
                refreshActiveTestSuites();
                ws.close();
                break;
              case 'log':
                addLog(data.level || 'info', data.message);
                break;
              default:
                console.log('Unknown WebSocket message type:', data.type);
            }
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          addLog('warning', 'Real-time monitoring connection lost, falling back to polling');
          // Fall back to polling if WebSocket fails
          fallbackToPolling(testRunId);
        };
        
        ws.onclose = () => {
          console.log('🔗 WebSocket disconnected');
          addLog('info', 'Real-time monitoring connection closed');
        };
        
        // Store WebSocket reference for cleanup
        websocketRef.current = ws;
        return ws;
      } catch (error) {
        console.error('Failed to establish WebSocket connection:', error);
        addLog('warning', 'Could not establish real-time monitoring, using fallback');
        fallbackToPolling(testRunId);
      }
    } else {
      // Fall back to polling if no WebSocket URL
      fallbackToPolling(testRunId);
    }
  };
  
  const fallbackToPolling = (testRunId: string) => {
    addLog('info', 'Using polling for test progress updates');
    const progressInterval = setInterval(() => {
      setTestProgress(prev => {
        const newProgress = prev + Math.random() * 10;
        if (newProgress >= 100) {
          clearInterval(progressInterval);
          setIsRunning(false);
          addLog('success', 'Test suite completed successfully');
          refreshActiveTestSuites();
          return 100;
        }
        return newProgress;
      });
    }, 3000);
  };
  
  const refreshActiveTestSuites = async () => {
    try {
      const suites = await aiTestingDashboardService.getActiveTestSuites();
      setActiveTestSuites(suites);
    } catch (error) {
      console.error('Failed to refresh test suites:', error);
    }
  };

  const toggleAgent = (agentId: string) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const getIconComponent = (iconName: string): React.ComponentType<any> => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      Shield,
      Heart,
      Activity,
      Camera,
      Brain,
      FileText,
      Mic,
      Layers,
      AlertCircle,
      Cpu,
      Globe,
      Terminal,
      Settings,
      Users,
      Zap,
      Cloud,
      CheckCircle2,
      Headphones
    };
    return iconMap[iconName] || Settings;
  };
  
  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      auth: '#3b82f6',
      voice: '#8b5cf6',
      content: '#10b981',
      media: '#ec4899',
      social: '#6366f1',
      commerce: '#f59e0b',
      output: '#ef4444',
      sync: '#6b7280',
      a11y: '#14b8a6',
      perf: '#f97316',
      security: '#06b6d4',
      platform: '#f43f5e',
      general: '#6b7280',
      forms: '#8b5cf6',
      ux: '#10b981',
      validation: '#f59e0b',
      workflow: '#ec4899'
    };
    return colors[category] || '#6b7280';
  };

  const getHealthColor = (health: number) => {
    if (health >= 95) return 'success' as const;
    if (health >= 85) return 'warning' as const;
    return 'error' as const;
  };

  const getLogColor = (level: string) => {
    switch (level) {
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      default: return '#6b7280';
    }
  };

  const tabLabels = ['Overview', 'Test Suites', 'Scheduler', 'Scenario Builder', 'Analytics', 'Reports'];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: spacing[6] }}>
      {/* Header */}
      <Box sx={{ mb: spacing[8] }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: spacing[3],
            mb: spacing[2]
          }}
        >
          <Terminal sx={{ fontSize: 40, color: colors.content.main }} />
          AI Testing Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
            Automated Testing Suite with Baby Boomer Optimization
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing[3] }}>
            <StatusBadge
              status={connectionStatus === 'connected' ? 'active' : 'error'}
              label={connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
              variant="filled"
            />
            <IconButton>
              <Settings />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 2, borderColor: colors.content.main, mb: spacing[6] }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.content.main,
              height: 3,
            }
          }}
        >
          {tabLabels.map((label, index) => (
            <Tab
              key={index}
              label={label}
              sx={{
                minHeight: 64,
                fontSize: '1rem',
                fontWeight: 600,
                '&.Mui-selected': {
                  color: colors.content.main,
                }
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 2 && (
        <AITestingScheduler />
      )}
      
      {activeTab === 3 && (
        <AITestingScenarioBuilder />
      )}

      {/* Only show overview content for tabs 0, 1, 4, 5 */}
      {[0, 1, 4, 5].includes(activeTab) && (
        <>
          {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: spacing[6] }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Total Test Suites"
            value={loading ? 0 : dashboardStats?.total_suites || 247}
            change={12}
            trend="up"
            icon="layers"
            color="content"
            subtitle="Last 30 days"
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Success Rate"
            value={loading ? 0 : dashboardStats?.success_rate || 94.2}
            change={2.3}
            trend="up"
            icon="check_circle"
            color="default"
            suffix="%"
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Active Tests"
            value={loading ? 0 : dashboardStats?.active_tests || 3}
            icon="trending_up"
            color="content"
            subtitle="Across all platforms"
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Failed Tests (24h)"
            value={loading ? 0 : dashboardStats?.failed_tests_24h || 7}
            icon="error"
            color="costs"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Create Test Suite Section */}
      <Card sx={{ mb: spacing[6] }}>
        <CardContent sx={{ p: spacing[6] }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: spacing[6] }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Create New Test Suite
              </Typography>
              <Typography color="textSecondary">
                Configure and launch automated test scenarios
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={runTestSuite}
              disabled={isRunning || selectedAgents.length === 0 || loading}
              startIcon={isRunning ? <Loader2 /> : <Play />}
              sx={{
                background: isRunning || selectedAgents.length === 0 || loading ? undefined : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 4,
                py: 1.5,
              }}
            >
              {isRunning ? 'Running Tests...' : loading ? 'Loading...' : 'Run Test Suite'}
            </Button>
          </Box>

          {/* Test Configuration */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Suite Name"
                defaultValue="Complete User Journey Test"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select defaultValue="all" label="Platform">
                  <MenuItem value="all">All Platforms</MenuItem>
                  <MenuItem value="ios">iOS</MenuItem>
                  <MenuItem value="android">Android</MenuItem>
                  <MenuItem value="web">Web</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Environment</InputLabel>
                <Select defaultValue="dev" label="Environment">
                  <MenuItem value="dev">Development</MenuItem>
                  <MenuItem value="staging">Staging</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Test Type</InputLabel>
                <Select defaultValue="full_journey" label="Test Type">
                  <MenuItem value="full_journey">Full User Journey</MenuItem>
                  <MenuItem value="smoke">Smoke Test</MenuItem>
                  <MenuItem value="regression">Regression</MenuItem>
                  <MenuItem value="custom">Custom Selection</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Agent Selection */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Select Testing Agents
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label={`${selectedAgents.length} agents selected`} size="small" />
                <Button size="small" color="primary">
                  Select All
                </Button>
              </Box>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Loader2 />
                <Typography sx={{ ml: 2 }}>Loading test agents...</Typography>
              </Box>
            ) : error ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}
            
            <Grid container spacing={2}>
              {testAgents.map((agent) => {
                const Icon = agent.icon;
                const isSelected = selectedAgents.includes(agent.id);
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={agent.id}>
                    <Card
                      onClick={() => toggleAgent(agent.id)}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: isSelected ? 'primary.dark' : 'grey.400',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                            <Avatar
                              sx={{
                                bgcolor: isSelected ? 'primary.light' : 'grey.200',
                                color: isSelected ? 'primary.contrastText' : 'text.secondary',
                              }}
                            >
                              <Icon />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {agent.name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                {agent.description}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: getCategoryColor(agent.category),
                                  }}
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                                  {agent.category}
                                </Typography>
                                <StatusBadge
                                  status={agent.health >= 95 ? 'success' : agent.health >= 85 ? 'warning' : 'error'}
                                  label={`${agent.health}%`}
                                  variant="outlined"
                                  size="sm"
                                />
                              </Box>
                            </Box>
                          </Box>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => {}}
                            color="primary"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Active Test Suites */}
      <Card sx={{ mb: spacing[6] }}>
        <CardContent sx={{ p: spacing[6] }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: spacing[6] }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Active Test Suites
              </Typography>
              <Typography color="textSecondary">
                Monitor running tests in real-time
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshCw />}
              onClick={refreshActiveTestSuites}
              sx={{ px: 3 }}
            >
              Refresh
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <Loader2 />
                <Typography sx={{ ml: 2 }}>Loading test suites...</Typography>
              </Box>
            ) : activeTestSuites.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <Activity sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>No active test suites</Typography>
                <Typography>Create a new test suite to get started</Typography>
              </Box>
            ) : (
              activeTestSuites.map((suite) => {
                const getStatusColor = (status: string) => {
                  switch (status) {
                    case 'running': return 'warning';
                    case 'completed': return 'success';
                    case 'failed': return 'error';
                    case 'queued': return 'info';
                    default: return 'default';
                  }
                };
                
                const getStatusIcon = (status: string) => {
                  switch (status) {
                    case 'running': return <Loader2 />;
                    case 'completed': return <CheckCircle2 />;
                    case 'failed': return <AlertTriangle />;
                    case 'queued': return <Clock />;
                    default: return <Settings />;
                  }
                };

                const shouldShowProgress = suite.status === 'running' && suite.progress !== undefined;
                const shouldShowError = suite.status === 'failed' && suite.error_message;
                const isCompleted = suite.status === 'completed';
                const isFailed = suite.status === 'failed';

                return (
                  <Card 
                    key={suite.id}
                    variant="outlined" 
                    sx={{ 
                      transition: 'box-shadow 0.2s', 
                      '&:hover': { boxShadow: 4 },
                      ...(isFailed && { 
                        border: 2, 
                        borderColor: 'error.light', 
                        bgcolor: 'error.50' 
                      })
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {suite.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            ID: {suite.id} • {suite.platform} • {suite.environment}
                            {suite.started_at && ` • Started ${new Date(suite.started_at).toLocaleString()}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <StatusBadge
                            status={
                              suite.status === 'completed' ? 'success' :
                              suite.status === 'failed' ? 'error' :
                              suite.status === 'running' ? 'warning' :
                              'pending'
                            }
                            label={suite.status.charAt(0).toUpperCase() + suite.status.slice(1)}
                            variant="filled"
                          />
                          <IconButton>
                            <MoreVertical />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {shouldShowProgress && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                              Progress
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {Math.round(suite.progress!)}%
                              {suite.total_tests && ` (${suite.passed_tests || 0}/${suite.total_tests} tests)`}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={suite.progress!}
                            sx={{
                              height: 12,
                              borderRadius: 6,
                              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            }}
                          />
                        </Box>
                      )}

                      {shouldShowError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="bold">
                            Error: {suite.error_message}
                          </Typography>
                          {suite.progress && (
                            <Typography variant="body2">
                              Failed after {Math.round(suite.progress)}% completion
                            </Typography>
                          )}
                        </Alert>
                      )}

                      {isCompleted && suite.total_tests && (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" fontWeight="bold">
                                {suite.passed_tests || 0}/{suite.total_tests}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Tests Passed
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" fontWeight="bold" color="success.main">
                                {suite.success_rate ? `${Math.round(suite.success_rate)}%` : '100%'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Success Rate
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" fontWeight="bold">
                                {suite.duration || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Duration
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" fontWeight="bold">
                                {suite.total_tests || 0}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Test Cases
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                          {suite.estimated_remaining && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Clock />
                              <Typography variant="body2" color="textSecondary">
                                Est. {suite.estimated_remaining} remaining
                              </Typography>
                            </Box>
                          )}
                          {suite.cpu_usage && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Cpu />
                              <Typography variant="body2" color="textSecondary">
                                CPU: {suite.cpu_usage}%
                              </Typography>
                            </Box>
                          )}
                          {suite.memory_usage && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Activity />
                              <Typography variant="body2" color="textSecondary">
                                Memory: {suite.memory_usage}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isFailed && (
                            <Button variant="outlined" startIcon={<RefreshCw />}>
                              Retry Test
                            </Button>
                          )}
                          {isCompleted && (
                            <Button variant="outlined" startIcon={<Download />}>
                              Download Report
                            </Button>
                          )}
                          <Button 
                            variant="contained" 
                            color={isFailed ? 'error' : isCompleted ? 'success' : 'primary'}
                            startIcon={isFailed ? <AlertCircle /> : isCompleted ? <BarChart3 /> : <Eye />}
                            sx={{ px: 3 }}
                          >
                            {isFailed ? 'View Error' : isCompleted ? 'View Report' : 'View Details'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Real-time Log */}
      <Card>
        <CardContent sx={{ p: spacing[6] }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: spacing[6] }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Real-time Test Log
              </Typography>
              <Typography color="textSecondary">
                Live stream of test execution events
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton>
                <Filter />
              </IconButton>
              <Button onClick={() => setLogs([])} variant="outlined">
                Clear Log
              </Button>
            </Box>
          </Box>

          <Paper
            ref={logContainerRef}
            sx={{
              bgcolor: '#1a1a1a',
              color: 'white',
              p: 2,
              height: 400,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            }}
          >
            {logs.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'grey.500' }}>
                <Terminal sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
                <Typography>Waiting for test events...</Typography>
              </Box>
            ) : (
              logs
                .filter(log => 
                  searchQuery === '' || log.message.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((log) => (
                  <Box
                    key={log.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      py: 0.5,
                      color: getLogColor(log.level),
                    }}
                  >
                    <Typography component="span" sx={{ color: 'grey.500', minWidth: 80 }}>
                      [{log.timestamp}]
                    </Typography>
                    <Typography component="span" sx={{ flex: 1 }}>
                      {log.message}
                    </Typography>
                  </Box>
                ))
            )}
          </Paper>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400' }} />
                <Typography variant="body2" color="textSecondary">Info</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant="body2" color="textSecondary">Success</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                <Typography variant="body2" color="textSecondary">Warning</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                <Typography variant="body2" color="textSecondary">Error</Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {logs.length} events
            </Typography>
          </Box>
        </CardContent>
      </Card>
        </>
      )}
    </Box>
  );
};

export default AITestingDashboard;